import { useForm } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import {
  ResponsiveDialog,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@/components/ui/responsive-dialog"

export function TrackFormDialog({ open, onOpenChange, track = null }) {
  const isEditing = !!track
  const form = useForm({
    title: track?.title || "",
    description: track?.description || "",
    sequential: track?.sequential ?? false,
    certifying: track?.certifying ?? false,
  })

  const handleSubmit = (e) => {
    e.preventDefault()

    if (isEditing) {
      form.patch(`/app/admin/learning_tracks/${track.id}`, {
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
      })
    } else {
      form.post("/app/admin/learning_tracks", {
        preserveScroll: true,
        onSuccess: () => onOpenChange(false),
      })
    }
  }

  const handleClose = (isOpen) => {
    if (!isOpen) {
      form.reset()
      form.clearErrors()
    }
    onOpenChange(isOpen)
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleClose}>
      <ResponsiveDialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle className="font-display text-xl">
              {isEditing ? "Edit Track" : "Create Learning Track"}
            </ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {isEditing
                ? "Update this learning track."
                : "Create a new learning track to bundle courses together."}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>

          <div className="space-y-4 py-4 px-4 sm:px-0">
            <div className="space-y-2">
              <Label htmlFor="track-title">
                Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="track-title"
                placeholder="Year 1 Curriculum"
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
                value={form.data.title}
                onChange={(e) => form.setData("title", e.target.value)}
                required
              />
              {form.errors.title && (
                <p className="text-sm text-red-600">{form.errors.title}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="track-description">Description</Label>
              <Textarea
                id="track-description"
                placeholder="Optional description of this track..."
                className="bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400 min-h-[80px]"
                value={form.data.description}
                onChange={(e) => form.setData("description", e.target.value)}
              />
            </div>

            <div className="space-y-3 rounded-lg border border-zinc-200 p-3">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Label htmlFor="track-sequential">Sequential</Label>
                  <p className="text-xs text-zinc-500">
                    Each course opens only after the one before it is complete.
                  </p>
                </div>
                <Switch
                  id="track-sequential"
                  checked={form.data.sequential}
                  onCheckedChange={(v) => form.setData("sequential", v)}
                />
              </div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <Label htmlFor="track-certifying">Certifying</Label>
                  <p className="text-xs text-zinc-500">
                    A member earns one certificate for the whole track when
                    they complete every course in it and pass every quiz. This
                    works the same with or without Sequential.
                  </p>
                </div>
                <Switch
                  id="track-certifying"
                  checked={form.data.certifying}
                  onCheckedChange={(v) => form.setData("certifying", v)}
                />
              </div>
            </div>
          </div>

          <ResponsiveDialogFooter className="px-4 sm:px-0">
            <ResponsiveDialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </ResponsiveDialogClose>
            <Button
              type="submit"
              variant="gradient"
              disabled={form.processing}
            >
              {form.processing
                ? "Saving..."
                : isEditing
                  ? "Save Changes"
                  : "Create Track"}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
